"use client";

import { rmcClient } from "@/lib/rmcClient";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useReactToPrint } from "react-to-print";

function pick(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v);
  }
  return "";
}

function fmtQty(v, digits = 3) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(digits);
}

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function hasValue(v) {
  return v !== undefined && v !== null && String(v).trim() !== "";
}

const PurchaseInvoicePrint = forwardRef(function PurchaseInvoicePrint({ invoice }, ref) {
  const contentRef = useRef(null);
  const [freshInvoice, setFreshInvoice] = useState(null);

  const printFn = useReactToPrint({
    contentRef,
    documentTitle: invoice?.invoice_no || "purchase-invoice",
  });

  const prepareFreshData = async () => {
    if (!invoice?.id) return;
    const full = (await rmcClient.getPurchaseInvoice(invoice.id)).data;
    flushSync(() => {
      setFreshInvoice(full);
    });
  };

  useImperativeHandle(ref, () => ({
    openPrint: async () => {
      if (!invoice) return;
      await prepareFreshData();
      await new Promise((resolve) => setTimeout(resolve, 0));
      printFn();
    },
  }));

  useEffect(() => {
    setFreshInvoice(null);
  }, [invoice?.id]);

  if (!invoice) {
    return <div ref={contentRef} className="hidden" aria-hidden />;
  }

  const source = freshInvoice || invoice;
  const items = (source?.challans || []).flatMap((ch) =>
    (ch?.items || []).map((line) => {
      const qty = Number(line.qty);
      const rate = Number(line.rate);
      const amount = Number(line.amount);
      return {
        challan_no: ch?.challan_no || "",
        label: line?.material?.name || "—",
        qty: Number.isFinite(qty) ? qty : 0,
        rate: Number.isFinite(rate) ? rate : 0,
        amount: Number.isFinite(amount) ? amount : 0,
        remark: line?.remark || "",
      };
    })
  );

  const subTotal = Number(source.sub_total);
  const gstRate = Number(source.gst_rate || 0);
  const gstAmount = Number(source.gst_amount);
  const totalAmount = Number(source.total_amount);
  const challanCount = Array.isArray(source?.challans) ? source.challans.length : 0;
  const isMultiChallan = challanCount > 1;

  const partyObj = source?.purchase_party || {};
  const partyName = pick(partyObj?.name);
  const partyAddress = pick(partyObj?.address, partyObj?.billing_address, partyObj?.address_line1);
  const partyPhone = pick(partyObj?.contact_no, partyObj?.phone, partyObj?.mobile_no, partyObj?.mobile);
  const partyGst = pick(partyObj?.gst_number, partyObj?.gstin);

  const invoiceNo = pick(source?.invoice_no, source?.id ? `PINV-${source.id}` : "");
  const invoiceDate = pick(source?.date, source?.invoice_date);
  const fyLabel = pick(source?.financial_year?.label);
  const challanList = (source?.challans || []).map((c) => c?.challan_no).filter(Boolean).join(", ");

  const leftInfoRows = [
    { label: "Invoice Number", value: invoiceNo },
    { label: "Invoice Date", value: invoiceDate },
    { label: "Financial Year", value: fyLabel },
  ].filter((row) => hasValue(row.value));

  const rightInfoRows = [{ label: "Challan No", value: challanList }]
    .filter((row) => hasValue(row.value) && !isMultiChallan);
  const hasRemarkColumn = items.some((it) => hasValue(it.remark));
  const hasTaxColumn = source?.tax_type === "GST";
  const totalColumnCount = 5 + (hasTaxColumn ? 1 : 0) + (hasRemarkColumn ? 1 : 0);
  const totalsLeftColSpan = Math.max(1, totalColumnCount - 2);
  const emptyFillHeightPx = Math.max(110, 645 - items.length * 34);

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed left-0 top-0 -z-10 h-0 w-0 overflow-hidden opacity-0 print:static print:z-50 print:h-auto print:w-auto print:overflow-visible print:opacity-100"
    >
      <div ref={contentRef} className="bg-white text-black print:block">
        <section className="mx-auto flex h-[297mm] w-[210mm] flex-col border border-slate-700 p-2 text-[10px] leading-tight">
          <div className="border border-slate-600">
            <div className="border-b border-slate-600 px-2 py-1.5 text-center">
              <p className="text-[9px] font-semibold uppercase tracking-wide">Tax Invoice</p>
              <p className="text-base font-bold leading-tight">PURCHASE INVOICE</p>
            </div>

            {(leftInfoRows.length > 0 || rightInfoRows.length > 0) && (
              <div className="grid grid-cols-2 border-b border-slate-600">
                <div className="border-r border-slate-600 px-2 py-2">
                  {leftInfoRows.map((row) => (
                    <div key={row.label} className="mb-1 flex gap-2 last:mb-0">
                      <span className="min-w-[95px] font-semibold">{row.label}</span>
                      <span>: {row.value}</span>
                    </div>
                  ))}
                </div>
                <div className="px-2 py-2">
                  {rightInfoRows.map((row) => (
                    <div key={row.label} className="mb-1 flex gap-2 last:mb-0">
                      <span className="min-w-[95px] font-semibold">{row.label}</span>
                      <span>: {row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(hasValue(partyName) || hasValue(partyAddress) || hasValue(partyPhone) || hasValue(partyGst)) && (
              <div className="grid grid-cols-2 border-b border-slate-600">
                <div className="border-r border-slate-600 px-2 py-2">
                  <p className="mb-1 font-semibold">Billing Details</p>
                  {hasValue(partyName) && (
                    <p>
                      <span className="font-semibold">Name</span>: {partyName}
                    </p>
                  )}
                  {(hasValue(partyPhone) || hasValue(partyGst)) && (
                    <p>
                      {hasValue(partyGst) && (
                        <>
                          <span className="font-semibold">GSTIN</span>: {partyGst}
                        </>
                      )}
                      {hasValue(partyGst) && hasValue(partyPhone) && " | "}
                      {hasValue(partyPhone) && (
                        <>
                          <span className="font-semibold">Mobile</span>: {partyPhone}
                        </>
                      )}
                    </p>
                  )}
                  {hasValue(partyAddress) && <p>{partyAddress}</p>}
                </div>
                <div className="px-2 py-2">
                  <p className="mb-1 font-semibold">Shipping Details</p>
                  <p>
                    <span className="font-semibold">Purchase Challans</span>: {(source?.challans || []).length}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-2">
            <table className="w-full border-collapse border border-slate-600">
              <thead>
                <tr className="bg-slate-100">
                  <th className="w-10 border border-slate-600 px-2 py-1 text-center">Sr.</th>
                  <th className="border border-slate-600 px-2 py-1 text-left">Item Description</th>
                  <th className="border border-slate-600 px-2 py-1 text-right">Quantity</th>
                  <th className="border border-slate-600 px-2 py-1 text-right">Rate</th>
                  {hasTaxColumn && <th className="w-16 border border-slate-600 px-2 py-1 text-right">Tax %</th>}
                  {hasRemarkColumn && <th className="border border-slate-600 px-2 py-1 text-left">Remark</th>}
                  <th className="border border-slate-600 px-2 py-1 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((line, idx) => (
                  <tr key={`${line.challan_no}-${idx}`}>
                    <td className="border border-slate-600 px-2 py-0.5 text-center align-top">{idx + 1}</td>
                    <td className="border border-slate-600 px-2 py-0.5 align-top">
                      {line.label}
                      {isMultiChallan && line.challan_no ? ` (${line.challan_no})` : ""}
                    </td>
                    <td className="border border-slate-600 px-2 py-0.5 text-right align-top">{fmtQty(line.qty) || "0.000"}</td>
                    <td className="border border-slate-600 px-2 py-0.5 text-right align-top">{fmtMoney(line.rate) || "0.00"}</td>
                    {hasTaxColumn && (
                      <td className="border border-slate-600 px-2 py-0.5 text-right align-top">{gstRate.toFixed(2)}</td>
                    )}
                    {hasRemarkColumn && <td className="border border-slate-600 px-2 py-0.5 align-top">{line.remark || ""}</td>}
                    <td className="border border-slate-600 px-2 py-0.5 text-right align-top">{fmtMoney(line.amount) || "0.00"}</td>
                  </tr>
                ))}
                <tr>
                  <td className="border border-slate-600 px-2 py-1" style={{ height: `${emptyFillHeightPx}px` }} />
                  <td className="border border-slate-600 px-2 py-1" />
                  <td className="border border-slate-600 px-2 py-1" />
                  <td className="border border-slate-600 px-2 py-1" />
                  {hasTaxColumn && <td className="border border-slate-600 px-2 py-1" />}
                  {hasRemarkColumn && <td className="border border-slate-600 px-2 py-1" />}
                  <td className="border border-slate-600 px-2 py-1" />
                </tr>
                <tr>
                  <td className="border border-slate-600 px-2 py-1" colSpan={totalsLeftColSpan} />
                  <td className="border border-slate-600 px-2 py-1 text-right font-semibold">Subtotal</td>
                  <td className="border border-slate-600 px-2 py-1 text-right">
                    {fmtMoney(Number.isFinite(subTotal) ? subTotal : 0)}
                  </td>
                </tr>
                {hasTaxColumn && (
                  <tr>
                    <td className="border border-slate-600 px-2 py-1" colSpan={totalsLeftColSpan} />
                    <td className="border border-slate-600 px-2 py-1 text-right font-semibold">
                      GST ({gstRate.toFixed(2)}%)
                    </td>
                    <td className="border border-slate-600 px-2 py-1 text-right">
                      {fmtMoney(Number.isFinite(gstAmount) ? gstAmount : 0)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="border border-slate-600 px-2 py-1" colSpan={totalsLeftColSpan} />
                  <td className="border border-slate-600 px-2 py-1 text-right font-semibold">Total</td>
                  <td className="border border-slate-600 px-2 py-1 text-right font-semibold">
                    {fmtMoney(Number.isFinite(totalAmount) ? totalAmount : 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-auto grid grid-cols-2 gap-2 pt-1">
            <div className="min-h-24 border border-slate-600 p-2 text-[10px]" />
            <div className="flex min-h-24 items-end justify-end border border-slate-600 p-2 text-[10px]">
              <p className="font-semibold">Authorized Signature</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
});

PurchaseInvoicePrint.displayName = "PurchaseInvoicePrint";

export default PurchaseInvoicePrint;
